import React, { useEffect, useState } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { editThemeUrl } from './merchantPass';
import { useMerchantBar } from './useMerchantBar';
import './merchantBar.css';

// Keeps the page's own foot out from under the bar while it shows.
function useFootRoom(shown: boolean): void {
  useEffect(() => {
    if (!shown) return;
    const before = document.body.style.paddingBottom;
    document.body.style.paddingBottom = '48px';
    return () => { document.body.style.paddingBottom = before; };
  }, [shown]);
}

// The ⋯ menu: hide the bar for this visit, or leave the merchant's view of the store altogether.
const MoreMenu: React.FC<{ onHide: () => void; onExit: () => void }> = ({ onHide, onExit }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="xs-mbar__more" aria-label="More" aria-expanded={open} aria-haspopup="menu" onClick={() => setOpen((o) => !o)}>
        <MoreHorizontal size={16} aria-hidden />
      </button>
      {open && (
        <div role="menu" className="xs-mbar__menu" onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}>
          <button type="button" role="menuitem" onClick={onHide}>Hide for this visit</button>
          <button type="button" role="menuitem" onClick={onExit}>Stop showing on this browser</button>
        </div>
      )}
    </>
  );
};

// Shopify's bar on a merchant's own store: the theme it wears, and Customize, which opens the editor on the page being
// looked at. Only a merchant who opened the store from the admin sees it, for a month from then.
export const MerchantBar: React.FC<{ page: string }> = ({ page }) => {
  const bar = useMerchantBar();
  useFootRoom(Boolean(bar.held));
  if (!bar.held) return null;
  return (
    <div className="xs-mbar" role="region" aria-label="Merchant bar">
      <span className="xs-mbar__theme">
        <img className="xs-mbar__mark" src="/favicon.svg" width={24} height={24} alt="" />
        <span className="xs-mbar__name">{bar.held.theme.name}</span>
      </span>
      <span className="xs-mbar__note">Only you see this bar</span>
      <span className="xs-mbar__actions">
        <MoreMenu onHide={bar.hide} onExit={bar.exit} />
        <a className="xs-mbar__button" href={editThemeUrl(bar.held, page)} target="_blank" rel="noopener">Customize</a>
      </span>
    </div>
  );
};
